import React, { ReactElement } from 'react';
import { Button } from '@odin-labs/components';
import {
  GetWorkerAccessRestorePreviewQuery,
  WorkerAccessRestorePreviewStatus,
} from 'apollo/generated/client-operations';

type Props = {
  loading: boolean;
  failed: boolean;
  preview:
    | GetWorkerAccessRestorePreviewQuery['workerAccessRestorePreview']
    | undefined;
  onRetry: () => void;
};

export function RestoreWorkerAccessPreview({
  loading,
  failed,
  preview,
  onRetry,
}: Props): ReactElement {
  if (failed) {
    return (
      <div role="alert" className="odin-mb-6">
        <p>
          Access groups could not be loaded. You can still restore site access.
          Saved groups will be restored only when the worker meets the site’s
          access requirements.
        </p>
        <Button
          type="button"
          text="Try again"
          onClick={onRetry}
          disabled={loading}
        />
      </div>
    );
  }
  if (loading) {
    return (
      <p role="status" className="odin-mb-6">
        Loading previous access groups… You can still restore site access.
      </p>
    );
  }
  if (!preview) {
    return (
      <p role="status" className="odin-mb-6">
        The access group preview is unavailable. You can still restore site
        access. Saved groups will be restored only when the worker meets the
        site’s access requirements.
      </p>
    );
  }
  const { status, groups, totalCount } = preview;
  switch (status) {
    case WorkerAccessRestorePreviewStatus.NotApplicable:
      return null;
    case WorkerAccessRestorePreviewStatus.Preparing:
      return (
        <p role="status" className="odin-mb-6">
          Access changes are still being processed. This preview will update
          shortly. You can still restore site access; saved groups will be
          restored only when the worker meets the site’s access requirements.
        </p>
      );
    case WorkerAccessRestorePreviewStatus.NoSnapshot:
      return (
        <p className="odin-mb-6">
          No saved access groups are available to restore. Removing this
          restriction may still leave other access requirements to complete.
        </p>
      );
    case WorkerAccessRestorePreviewStatus.Ready:
      return (
        <section
          aria-label="Access groups to restore"
          className="odin-mb-6 odin-space-y-2"
        >
          <p className="odin-font-medium">Previous access groups</p>
          {totalCount ? (
            <>
              <p>
                These groups will be restored when this worker meets the site’s
                access requirements:
              </p>
              <ul className="odin-list-disc odin-pl-5">
                {groups.map((group) => (
                  <li key={group.id}>{group.name}</li>
                ))}
              </ul>
              {totalCount > groups.length && (
                <p>And {totalCount - groups.length} more groups.</p>
              )}
            </>
          ) : (
            <p>No saved access groups remain available to restore.</p>
          )}
          <p>
            Other bans or site requirements may still prevent access. Reader
            access updates after syncing.
          </p>
        </section>
      );
    default: {
      const exhaustive: never = status;
      return exhaustive;
    }
  }
}
